import React, { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";

// ─────────────────────────────────────────────────────────────────────────────
// 1. SINGLE PRODUCTION RECORD PRINT (Formulation / Batch Ticket)
// ─────────────────────────────────────────────────────────────────────────────
export const ProductionRecordPrint = React.forwardRef(
  ({ data, actualData }, ref) => {
    const barcodeRef = useRef(null);

    // Fallback to ID if production number is missing
    const prodNumber =
      data?.productionNumber || (data?.id ? `PRD-${data.id}` : "");

    useEffect(() => {
      if (prodNumber && barcodeRef.current) {
        try {
          JsBarcode(barcodeRef.current, prodNumber, {
            format: "CODE128",
            lineColor: "#000000",
            width: 1.5,
            height: 40,
            displayValue: false,
            margin: 0,
          });
        } catch (e) {
          console.error("Barcode error", e);
        }
      }
    }, [prodNumber]);

    if (!data) return null;

    // If actualData is passed (from the modal), use it. Otherwise, use planned entries.
    const isActual = data.hasActualMeasurements;
    const entriesToPrint = actualData?.entries || data.entries || [];
    const displayTotalWeight = isActual
      ? actualData?.totalActualWeight || data.totalActualWeight
      : 0;

    const documentTitle = isActual
      ? "ACTUAL PRODUCTION RECORD"
      : "PLANNED BATCH TICKET";

    const prodDate = data.productionDate;

    return (
      <div
        ref={ref}
        className="p-8 bg-white text-black text-xs font-sans max-w-[800px] mx-auto"
      >
        {/* ── HEADER ── */}
        <div className="text-center mb-6">
          <h1 className="text-xl font-bold uppercase tracking-wide">
            Ichthus Technology
          </h1>
          <div className="text-[10px] text-gray-600 mb-2">
            <p># 3128 Sto. Rosario Street, Barangay</p>
            <p>Mapulang Lupa, Valenzuela, 1440 Metro</p>
          </div>
          <div className="mt-2 flex justify-center">
            <svg ref={barcodeRef}></svg>
          </div>
          <p className="text-[10px] font-bold mt-1 tracking-widest">
            {prodNumber}
          </p>
        </div>

        {/* ── INFO GRID ── */}
        <div className="flex justify-between items-start mb-6 gap-6">
          {/* Production Details */}
          <div className="w-1/2">
            <h2 className="font-bold text-sm uppercase mb-1 bg-gray-100 p-1 border-l-2 border-black pl-2">
              Production Details
            </h2>
            <div className="pl-1 space-y-1.5 mt-2">
              <p>
                <span className="font-bold text-gray-600 uppercase text-[10px]">
                  Reference / DO:
                </span>
                <br />
                <span className="font-bold text-sm uppercase text-gray-900">
                  {data.deliveryOrderNumber || "N/A"}
                </span>
              </p>
              <p>
                <span className="font-bold text-gray-600 uppercase text-[10px]">
                  Warehouse Source:
                </span>
                <br />
                <span className="uppercase text-gray-800 font-semibold">
                  {data.warehouseName || "N/A"}
                </span>
              </p>
              <p>
                <span className="font-bold text-gray-600 uppercase text-[10px]">
                  Prepared By:
                </span>
                <br />
                <span className="uppercase text-gray-800">
                  {data.createdBy || "System User"}
                </span>
              </p>
            </div>
          </div>

          {/* Document Details */}
          <div className="w-1/2">
            <h2 className="font-bold text-lg mb-2 text-right uppercase tracking-tight">
              {documentTitle}
            </h2>
            <div className="grid grid-cols-2 gap-y-1 text-xs text-right">
              <span className="font-bold text-gray-500 uppercase text-[10px]">
                Production No.
              </span>
              <span className="font-bold text-black">{prodNumber}</span>

              <span className="font-bold text-gray-500 uppercase text-[10px]">
                Date
              </span>
              <span className="font-bold">
                {prodDate
                  ? new Date(prodDate).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "—"}
              </span>

              <span className="font-bold text-gray-500 uppercase text-[10px]">
                Weigh-in Status
              </span>
              <span
                className={`uppercase font-bold ${isActual ? "text-emerald-700" : "text-amber-600"}`}
              >
                {isActual ? "MEASURED" : "PENDING ACTUALS"}
              </span>
            </div>

            <div className="mt-4 bg-gray-50 p-2 border border-gray-200">
              <div className="flex justify-between font-bold text-sm items-center">
                <span className="text-gray-600 uppercase text-[10px] tracking-wider">
                  Total Weight:
                </span>
                <span
                  className={isActual ? "text-emerald-700" : "text-gray-800"}
                >
                  {displayTotalWeight > 0
                    ? `${Number(displayTotalWeight).toLocaleString("en-PH", { minimumFractionDigits: 2 })} kg`
                    : "—"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {data.remarks && (
          <div className="mb-4 text-[11px] bg-gray-50 p-2 border-l-2 border-amber-400">
            <span className="font-bold uppercase text-gray-500 text-[10px]">
              Remarks:
            </span>
            <p className="font-medium text-gray-800 mt-0.5">{data.remarks}</p>
          </div>
        )}

        {/* ── TABLE HEADER ── */}
        <div className="mt-4 border-b-2 border-black pb-1 mb-1 bg-gray-100 flex font-bold text-[10px] uppercase p-1">
          <div className="w-8 text-center">#</div>
          <div className="w-24 pl-2">Tag/Group</div>
          <div className="flex-1">Material Description</div>
          <div className="w-24 text-right pr-2">Quantity</div>
          <div className="w-12 text-center">Unit</div>
          <div className="w-28 text-center bg-white border border-gray-300 ml-2">
            Actual Weigh-in
          </div>
        </div>

        {/* ── TABLE BODY ── */}
        <div className="min-h-[300px] font-sans tracking-tight">
          <div className="divide-y divide-gray-100 border-b border-gray-200">
            {entriesToPrint.length === 0 ? (
              <div className="py-4 text-center text-gray-400 italic">
                No formulation data found.
              </div>
            ) : (
              entriesToPrint.map((item, index) => {
                const qty = item.quantity
                  ? Number(item.quantity).toLocaleString("en-PH", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : "0.00";
                const tag = item.tagName || "—";
                const unit = item.unit || "kg";

                return (
                  <div
                    key={index}
                    className="flex items-center py-2 text-[10px] uppercase font-bold leading-snug"
                  >
                    <div className="w-8 text-center text-gray-400">
                      {(index + 1).toString().padStart(2, "0")}
                    </div>

                    <div className="w-24 text-indigo-700 truncate px-2 tracking-wider">
                      {tag}
                    </div>

                    <div
                      className="flex-1 text-gray-800 pr-2 truncate"
                      title={item.productName}
                    >
                      {item.productName || "Unknown Material"}
                    </div>

                    <div className="w-24 text-right text-gray-800 tabular-nums pr-2">
                      {qty}
                    </div>

                    <div className="w-12 text-center text-gray-500">{unit}</div>

                    {/* Blank line for production staff to write the actual scale reading if not yet measured */}
                    <div className="w-28 px-2 ml-2 flex items-end">
                      <div className="border-b border-gray-400 h-4 w-full"></div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ── SIGNATURES ── */}
        <div className="mt-16 grid grid-cols-3 gap-8 text-[10px]">
          {[
            "Prepared By (Formulation):",
            "Weighed By (Operator):",
            "Checked & Verified By:",
          ].map((label) => (
            <div key={label}>
              <div className="border-b border-black mb-1 h-6"></div>
              <p className="font-bold text-gray-700 uppercase tracking-wide text-[9px]">
                {label}
              </p>
              <p className="text-[8px] text-gray-400 mt-0.5">
                Signature over printed name
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center text-[9px] text-gray-400">
          System Generated {documentTitle} ({prodNumber}) | Printed on{" "}
          {new Date().toLocaleString()}
        </div>
      </div>
    );
  },
);

// ─────────────────────────────────────────────────────────────────────────────
// 2. GENERAL REPORT PRINT (For printing a filtered list/summary)
// ─────────────────────────────────────────────────────────────────────────────
export const ProductionListPrint = React.forwardRef(
  ({ dataList, filters }, ref) => {
    if (!dataList || dataList.length === 0) return null;

    const totalWeighed = dataList.filter((d) => d.hasActualMeasurements).length;

    return (
      <div
        ref={ref}
        className="p-8 bg-white text-black text-xs font-sans max-w-[1000px] mx-auto landscape"
      >
        {/* ── HEADER ── */}
        <div className="flex justify-between items-end mb-6 border-b-2 border-black pb-4">
          <div>
            <h1 className="text-xl font-black uppercase tracking-wide">
              Ichthus Technology
            </h1>
            <h2 className="text-sm font-bold mt-1 uppercase text-gray-700">
              Production Records Summary
            </h2>
            {filters && (
              <p className="text-[10px] text-gray-500 mt-1 font-medium">
                Filters: {filters}
              </p>
            )}
          </div>
          <div className="text-right text-[10px]">
            <p className="font-bold text-gray-600">
              Total Records:{" "}
              <span className="text-black text-sm">{dataList.length}</span>
            </p>
            <p className="font-bold text-emerald-600 mt-0.5">
              Measured: {totalWeighed} | Pending:{" "}
              {dataList.length - totalWeighed}
            </p>
          </div>
        </div>

        {/* ── TABLE ── */}
        <table className="w-full text-left text-[10px] border-collapse">
          <thead>
            <tr className="bg-gray-100 border-y border-gray-300 text-gray-800 uppercase tracking-wider text-[9px]">
              <th className="py-2 px-2">Production #</th>
              <th className="py-2 px-2">Ref Order (DO)</th>
              <th className="py-2 px-2">Date</th>
              <th className="py-2 px-2">Warehouse Source</th>
              <th className="py-2 px-2 text-center">Items</th>
              <th className="py-2 px-2 text-right">Total Weight</th>
              <th className="py-2 px-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {dataList.map((item, index) => {
              const itemsCount = item.entries ? item.entries.length : 0;
              const isMeasured = item.hasActualMeasurements;
              const weight =
                item.totalActualWeight > 0
                  ? `${Number(item.totalActualWeight).toLocaleString("en-PH", { minimumFractionDigits: 2 })} kg`
                  : "—";

              return (
                <tr key={item.id || index} className="hover:bg-gray-50">
                  <td className="py-2 px-2 font-bold font-mono text-indigo-700">
                    {item.productionNumber || `PRD-${item.id}`}
                  </td>
                  <td className="py-2 px-2 text-gray-600 font-semibold uppercase">
                    {item.deliveryOrderNumber || "—"}
                  </td>
                  <td className="py-2 px-2 text-gray-700">
                    {item.productionDate
                      ? new Date(item.productionDate).toLocaleDateString(
                          "en-US",
                          { year: "numeric", month: "short", day: "numeric" },
                        )
                      : "—"}
                  </td>
                  <td className="py-2 px-2 font-bold text-gray-800 uppercase">
                    {item.warehouseName || "—"}
                  </td>
                  <td className="py-2 px-2 text-center font-bold text-gray-600 tabular-nums">
                    {itemsCount}
                  </td>
                  <td
                    className={`py-2 px-2 text-right font-black tabular-nums ${isMeasured ? "text-emerald-700" : "text-gray-400"}`}
                  >
                    {weight}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <span
                      className={`uppercase font-bold tracking-wider text-[9px] ${isMeasured ? "text-emerald-600" : "text-amber-500"}`}
                    >
                      {isMeasured ? "Measured" : "Pending"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="mt-8 text-center text-[9px] text-gray-400 border-t border-gray-200 pt-2">
          Printed on {new Date().toLocaleString()} by System
        </div>
      </div>
    );
  },
);
